// Klient uwierzytelniania (django-allauth headless, tryb przeglądarkowy).
//
// Tryb przeglądarkowy = sesja w ciasteczku HttpOnly, a NIE token w localStorage.
// Świadomy wybór: token w localStorage jest do wykradzenia przez dowolny XSS,
// a ciasteczka HttpOnly JavaScript nie odczyta. Cena: trzeba obsłużyć CSRF.
//
// Dlatego każde żądanie zmieniające stan wymaga nagłówka X-CSRFToken. Token
// przychodzi w zwykłym ciasteczku `csrftoken` (celowo czytelnym dla JS) i jest
// ustawiany przez pierwsze wywołanie /auth/session.

import { apiFetch } from './apiFetch'
const BASE = process.env.REACT_APP_AUTH_API_URL || '/api/auth/browser/v1'

const readCookie = (name) => {
  const match = document.cookie.match(new RegExp(`(^|;\\s*)${name}=([^;]*)`))
  return match ? decodeURIComponent(match[2]) : null
}

/**
 * Odpowiedzi allauth mają stały kształt:
 *   { status, data: {...}, meta: { is_authenticated }, errors: [...] }
 * Kody HTTP bywają mylące — 401 przy rejestracji znaczy „konto powstało, ale
 * czekasz na potwierdzenie adresu", a nie „błąd". Dlatego NIE opieramy się na
 * samym kodzie, tylko na treści.
 */
const request = async (path, { method = 'GET', body } = {}) => {
  const headers = { 'Content-Type': 'application/json' }
  if (method !== 'GET') {
    const csrf = readCookie('csrftoken')
    if (csrf) headers['X-CSRFToken'] = csrf
  }

  let res
  try {
    res = await apiFetch(`${BASE}${path}`, {
      method,
      headers,
      credentials: 'same-origin', // bez tego ciasteczko sesji nie poleci
      body: body ? JSON.stringify(body) : undefined,
    })
  } catch {
    // Brak sieci to nie to samo co odrzucenie przez serwer — użytkownik musi
    // wiedzieć, że ma spróbować ponownie, a nie że podał złe dane.
    return { ok: false, networkError: true, errors: [{ message: 'Brak połączenia z serwerem. Spróbuj ponownie.' }] }
  }

  let payload = {}
  try {
    payload = await res.json()
  } catch {
    payload = {}
  }

  return {
    ok: res.ok,
    httpStatus: res.status,
    data: payload.data || {},
    meta: payload.meta || {},
    errors: payload.errors || [],
    flows: (payload.data && payload.data.flows) || [],
  }
}

/** Inicjuje sesję i pobiera ciasteczko CSRF. Wołane raz, przy starcie aplikacji. */
export const fetchSession = () => request('/auth/session')

/**
 * Rejestracja. Poza adresem i hasłem przyjmuje OPCJONALNY profil i firmę.
 *
 * Profil idzie tutaj, a nie osobnym żądaniem po rejestracji, bo potwierdzenie
 * adresu jest obowiązkowe — zaraz po `signup` nie ma jeszcze sesji, więc nie ma
 * czym uwierzytelnić `PATCH /account/me`.
 *
 * Nazwy pól MUSZĄ zgadzać się z `accounts/forms.py` (VoltValueSignupForm). Pole
 * nieznane temu formularzowi allauth po cichu ignoruje, więc literówka nie
 * zwróci żadnego błędu — dane po prostu nie dojdą.
 *
 * Dane firmy wysyłamy tylko jako PODPOWIEDŹ: serwer odpytuje rejestr sam i to
 * jego odpowiedź rozstrzyga, czy firma jest zweryfikowana. Nasze pola liczą się
 * wyłącznie wtedy, gdy rejestr nie odpowie.
 */
export const signup = ({
  email, password, acceptTerms, marketingConsent,
  userTypes, userTypeOther, accountType, company,
}) =>
  request('/auth/signup', {
    method: 'POST',
    body: {
      email,
      password,
      accept_terms: acceptTerms,
      marketing_consent: marketingConsent,
      user_types: userTypes || [],
      user_type_other: userTypeOther || '',
      account_type: accountType || '',
      company_nip: (company && company.nip) || '',
      company_name: (company && company.name) || '',
      company_street: (company && company.street) || '',
      company_postal_code: (company && company.postal_code) || '',
      company_city: (company && company.city) || '',
      company_regon: (company && company.regon) || '',
    },
  })

export const login = ({ email, password }) =>
  request('/auth/login', { method: 'POST', body: { email, password } })

export const logout = () => request('/auth/session', { method: 'DELETE' })

export const verifyEmail = (key) =>
  request('/auth/email/verify', { method: 'POST', body: { key } })

export const requestPasswordReset = (email) =>
  request('/auth/password/request', { method: 'POST', body: { email } })

export const resetPassword = ({ key, password }) =>
  request('/auth/password/reset', { method: 'POST', body: { key, password } })

/** Czy w odpowiedzi jest oczekujący krok o danym identyfikatorze. */
export const hasPendingFlow = (response, flowId) =>
  (response.flows || []).some((f) => f.id === flowId && f.is_pending)

/**
 * Komunikaty błędów z allauth przychodzą po angielsku. Tłumaczymy te, które
 * użytkownik realnie zobaczy; reszta leci w oryginale, żeby nigdy nie zgubić
 * informacji o przyczynie (lepszy komunikat po angielsku niż „coś poszło nie tak").
 */
const MESSAGES = {
  'A user is already registered with this email address.':
    'Konto z tym adresem już istnieje. Zaloguj się albo zresetuj hasło.',
  'The email address and/or password you specified are not correct.':
    'Nieprawidłowy adres e-mail lub hasło.',
  'Too many failed login attempts. Try again later.':
    'Zbyt wiele nieudanych prób logowania. Odczekaj kilka minut i spróbuj ponownie.',
  'This password is too short. It must contain at least 8 characters.':
    'Hasło jest za krótkie — potrzeba co najmniej 8 znaków.',
  'This password is too common.':
    'To hasło jest zbyt popularne. Wybierz mniej oczywiste.',
  'This password is entirely numeric.':
    'Hasło nie może składać się z samych cyfr.',
  'Enter a valid email address.': 'Podaj poprawny adres e-mail.',
  'Incorrect input.': 'Nieprawidłowe dane.',
  'Invalid or expired key.': 'Link jest nieprawidłowy albo wygasł. Poproś o nowy.',
}

export const translateError = (message) => MESSAGES[message] || message

export const errorText = (response, fallback = 'Nie udało się wykonać operacji.') => {
  const list = response.errors || []
  if (list.length) return list.map((e) => translateError(e.message)).join(' ')

  // Odpowiedzi BEZ listy błędów — najczęściej odrzucone przez warstwę niżej niż
  // walidacja formularza. Ogólne „nie udało się" jest tu bezużyteczne: nie mówi
  // ani co się stało, ani czy warto próbować ponownie.
  if (response.httpStatus === 403) {
    return 'Rejestracja jest chwilowo niedostępna albo Twoja sesja wygasła. '
      + 'Odśwież stronę i spróbuj ponownie.'
  }
  if (response.httpStatus === 429) {
    return 'Zbyt wiele prób z tego adresu. Odczekaj chwilę i spróbuj ponownie.'
  }
  if (response.httpStatus >= 500) {
    return 'Błąd po naszej stronie. Spróbuj ponownie za chwilę.'
  }
  return fallback
}
